import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImportPreview = ({ 
  previewData, 
  fileName, 
  onConfirmImport, 
  onCancel, 
  isProcessing 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const totalRecords = previewData?.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = previewData?.slice(startIndex, endIndex);

  const validRecords = previewData?.filter(record => record?.isValid);
  const invalidRecords = previewData?.filter(record => !record?.isValid);
  const duplicateRecords = previewData?.filter(record => record?.isDuplicate);

  const getStatusIcon = (record) => {
    if (!record?.isValid) return { icon: 'AlertCircle', color: 'text-error' };
    if (record?.isDuplicate) return { icon: 'AlertTriangle', color: 'text-warning' };
    return { icon: 'CheckCircle', color: 'text-success' };
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="FileText" size={20} className="mr-2" />
              Prévia da importação
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              File: {fileName}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
              Cancelar
            </Button>
            <Button 
              variant="default" 
              onClick={onConfirmImport}
              loading={isProcessing}
              iconName="Upload"
              iconPosition="left"
              disabled={validRecords?.length === 0}
            >
              Importar {validRecords?.length} registros
            </Button>
          </div>
        </div>
      </div>
      {/* Statistics */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-foreground">{totalRecords}</div>
            <div className="text-sm text-muted-foreground">Total de registros</div>
          </div>
          <div className="text-center p-4 bg-success/10 rounded-lg">
            <div className="text-2xl font-bold text-success">{validRecords?.length}</div>
            <div className="text-sm text-muted-foreground">Registros válidos</div>
          </div>
          <div className="text-center p-4 bg-error/10 rounded-lg">
            <div className="text-2xl font-bold text-error">{invalidRecords?.length}</div>
            <div className="text-sm text-muted-foreground">Registros inválidos</div>
          </div>
          <div className="text-center p-4 bg-warning/10 rounded-lg">
            <div className="text-2xl font-bold text-warning">{duplicateRecords?.length}</div>
            <div className="text-sm text-muted-foreground">Duplicados</div>
          </div>
        </div>
      </div>
      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Nome completo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                CPF
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Título de eleitor
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                E-mail
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Telefone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Problemas
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {currentRecords?.map((record, index) => {
              const status = getStatusIcon(record);
              return (
                <tr key={startIndex + index} className="hover:bg-muted/50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <Icon name={status?.icon} size={20} className={status?.color} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                    {record?.fullName || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                    {record?.cpf || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                    {record?.voterId || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                    {record?.email || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                    {record?.phone || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {record?.errors && record?.errors?.length > 0 ? (
                      <div className="space-y-1">
                        {record?.errors?.map((error, errorIndex) => (
                          <div key={errorIndex} className="text-xs text-error">
                            {error}
                          </div>
                        ))}
                      </div>
                    ) : record?.isDuplicate ? (
                      <div className="text-xs text-warning">Registro duplicado</div>
                    ) : (
                      <div className="text-xs text-success">Válido</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Exibindo {startIndex + 1} a {Math.min(endIndex, totalRecords)} de {totalRecords} registros
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
              >
                Anterior
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPreview;